import React, { useState } from "react";
import { validateEmail, validateLogin } from "../../helpers/Validator";
import { IMAGES } from "../../assets/Images/Image";
import "./Login.css";
import { useTranslation } from "react-i18next";
import { CACHE_KEYS, CacheManager } from "../../services/local/CacheManager";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/slices/AuthSlice";
import { FiMail, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Dialog from "../components/Dialog";
import { CONSTANTS } from "../../utils/Constants";
import { WSAuthentication } from "../../services/remote/authentication/WSAuthentication";

const ForgetPassword: React.FC = () => {
  const cache = CacheManager.getInstance();
  const authInstance = WSAuthentication.getInstance();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [errors, setErrors] = useState<any>({});
  const [serverError, setServerError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError("");
    setErrors({});

    const validationErrors = validateEmail(email);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsLoading(true);
      const user = await authInstance.forgetPassword(email);
      setDialogTitle(t("login.reset_password"));
      setDialogMessage(t("login.reset_password_message"));
      setOpen(true);
    } catch (error: any) {
      switch (error.message) {
        case CONSTANTS.STRINGS.EMAIL_NOT_FOUND: {
          setServerError(t("login.email_not_found"));
          setOpen(true);
          break;
        }
        default: {
          setServerError(t("login.unexpected_error"));
          setOpen(true);
          break;
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="header">
        <LanguageSwitcher />
      </div>

      <div className="formContainer">
        <form className="form" onSubmit={handleSubmit} noValidate>
          <h1 className="mc-title">{t("login.forget_password")}</h1>
          <p className="text"></p>

          {serverError && <div className="error-server">{serverError}</div>}

          <div className="formGroup">
            <label htmlFor="email" className="label">
              {t("login.email")}
            </label>
            <div className="input-container">
              <FiMail className="input-icon" />
              <input
                id="email"
                type="email"
                placeholder={t("login.email_placeholder")}
                value={email}
                className={`input ${errors.email ? "input-error" : ""}`}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
              />
            </div>
            {errors.email && <span className="error">{errors.email}</span>}
          </div>

          <button type="submit" className="mc-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="loading"></span>
                {t("login.logging_in")}
              </>
            ) : (
              t("login.login_button")
            )}
          </button>

          <a href="/login" className="forgotPassword">
            {t("login.back_to_login")}
          </a>
        </form>
      </div>

      <div className="infoContainer">
        <img
          src={IMAGES.AUTH_BACKGROUND || "/default-auth-bg.jpg"}
          className="image"
          alt="Authentication Background"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = "/default-auth-bg.jpg";
          }}
        />
        <div className="infoTextContainer">
          <h2 className="infoTitle">{t("login.welcome_back")}</h2>
          <p className="infoDescription">{t("login.welcome_message")}</p>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={dialogTitle}
        description={dialogMessage}
        showYesButton={false}
        showOkButton={true}
        negativeButtonText={t("ok")}
      />
    </div>
  );
};

export default ForgetPassword;
